import { useFlags as useFrameworkFlags } from "@oysterhr/feature-flags"
import { PropsWithChildren, useCallback, useMemo } from "react"
import { Outlet, useOutletContext } from "react-router-dom"
import { MissingPage } from "~/shared/pages/system"

export enum FlagCheckMode {
  And = "And",
  Or = "Or",
}

type Props = {
  flags: Record<string, boolean>
  mode?: FlagCheckMode
  /**
   * Use this to render a custom fallback component instead of the MissingPage
   * This can be used to render an old version of the page if the flag is not enabled
   */
  fallback?: JSX.Element
}

const FlagProtectedRoute = ({
  flags,
  mode = FlagCheckMode.And,
  children,
  fallback,
}: PropsWithChildren<Props>) => {
  const parentContext = useOutletContext()
  const frameworkFlags: Record<string, boolean> = useFrameworkFlags()

  const hasFlag = useCallback(
    ([flag, value]: [string, boolean]) => {
      return frameworkFlags[flag] === value
    },
    [frameworkFlags],
  )

  const canRenderPage = useMemo(() => {
    const flagsArray = Object.entries(flags)
    return mode === FlagCheckMode.And ? flagsArray?.every(hasFlag) : flagsArray?.some(hasFlag)
  }, [flags, mode, hasFlag])

  if (!canRenderPage) {
    return fallback ?? <MissingPage />
  }

  return children ? <>{children}</> : <Outlet context={parentContext} />
}

export default FlagProtectedRoute
